/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Vu Dinh Luan (vdluan@rsupport.com)
 *
 * @description
 *
 * @created at Fri Oct 04 2024
 **/
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

import { useEventHandler } from "@rfice/eventhandler";
import { useMeeting } from "@/frameworks/core/API";
import { EventID } from "@/frameworks/core/commons";
import { useAtomCallback } from "jotai/utils";
import { JotaiStore } from "@/frameworks/libraries/commons";

import { ConferenceRoomNumberAtom } from "./atom";

export default function OpenConferenceStart() {
	const meetingAPI = useMeeting();

	const { data: dataGroupConference, refetch } = useQuery<SwaggerMeetingAPIModels.GroupConferences[]>({
		queryKey: ["conference:list"],
		queryFn: async () => await meetingAPI.group.list(),
		refetchOnMount: true
	});

	const conferenceData = useMemo(() => {
		return dataGroupConference || [];
	}, [dataGroupConference]);

	const conferenceListRoomsNumber = useMemo(() => {
		const groupConferences = conferenceData
			.filter((item) => item.conferences && item.conferences.length > 0)
			.flatMap((item) => item.conferences?.map((conference) => conference.roomNumber))
			.filter((num): num is number => num !== undefined);

		return groupConferences || [];
	}, [conferenceData]);

	const generateRandomRoomNumber = useCallback(
		(numArr: number[]): number => {
			const randomNumber = Math.floor(Math.random() * 99) + 1;
			if (numArr.includes(randomNumber)) {
				return generateRandomRoomNumber(numArr);
			}
			return randomNumber;
		},
		[conferenceListRoomsNumber]
	);

	const handleOpenConferenceStart = useAtomCallback(
		useCallback(
			async (_, set, args: { num: number; groupId: string }) => {
				await refetch();
				if (args.num && args.num !== 0) {
					if (!conferenceListRoomsNumber.includes(args.num)) {
						set(ConferenceRoomNumberAtom, args.num);
						return;
					}
				}
				const newRoomNumber = generateRandomRoomNumber(conferenceListRoomsNumber);
				set(ConferenceRoomNumberAtom, newRoomNumber);
			},
			[conferenceListRoomsNumber, generateRandomRoomNumber]
		),
		{
			store: JotaiStore
		}
	);

	useEventHandler(EventID.OPEN_CONFERENCE_START, handleOpenConferenceStart, []);

	return null;
}
